import { useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './button';
import { Modal, ModalHeader, ModalBody, ModalFooter } from './Modal';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'default';
  isLoading?: boolean;
}

export default function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  isLoading = false
}: ConfirmationModalProps) {
  const handleClose = useCallback(() => {
    if (!isLoading) {
      onClose();
    }
  }, [isLoading, onClose]);

  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  const getVariantClasses = () => {
    switch (variant) {
      case 'danger':
        return {
          icon: 'text-red-600 dark:text-red-400',
          confirmButton: 'bg-red-600 hover:bg-red-700 text-white'
        };
      case 'warning':
        return {
          icon: 'text-amber-600 dark:text-amber-400',
          confirmButton: 'bg-amber-600 hover:bg-amber-700 text-white'
        };
      default:
        return {
          icon: 'text-primary dark:text-primary-light',
          confirmButton: 'bg-primary hover:bg-primary/90 text-white'
        };
    }
  };

  const variantClasses = getVariantClasses();

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      canClose={!isLoading}
      size="md"
    >
      <ModalHeader
        title={title}
        icon={<AlertTriangle className={`w-6 h-6 ${variantClasses.icon}`} />}
      />

      <ModalBody>
        <p className="text-gray-600 dark:text-gray-300 whitespace-pre-line">
          {message}
        </p>
      </ModalBody>

      <ModalFooter>
        <Button
          type="button"
          variant="outline"
          onClick={handleClose}
          disabled={isLoading}
        >
          {cancelText}
        </Button>
        <Button
          type="button"
          onClick={handleConfirm}
          disabled={isLoading}
          className={variantClasses.confirmButton}
        >
          {isLoading ? 'Processing...' : confirmText}
        </Button>
      </ModalFooter>
    </Modal>
  );
}